'use client';

import { IconButton } from '@components/button';
import { ClipboardCopyIcon } from '@radix-ui/react-icons';
import type { ReactElement } from 'react';
import { useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { PrismLight } from 'react-syntax-highlighter';
import bash from 'react-syntax-highlighter/dist/esm/languages/prism/bash';
import { materialOceanic } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { validateString } from 'utils/validate-string';

import styles from './code-block.module.scss';

PrismLight.registerLanguage('bash', bash);
PrismLight.registerLanguage('typescript', bash);
PrismLight.registerLanguage('tsx', bash);

const LANGUAGE_TO_PRISM_LANGUAGE: Record<string, string | undefined> = {
  sh: 'bash',
};

const LANGUAGE_TO_EXTENSION: Record<string, string | undefined> = {
  typescript: 'ts',
};

export interface CodeBlockProps {
  readonly code: string;
  readonly language: string;
  readonly filename?: string | undefined;
}

const rootClass = validateString(styles.root);
const topBarClass = validateString(styles.topBar);
const macIcons = validateString(styles.macIcons);
const topBarContentClass = validateString(styles.topBarContent);
const languageClass = validateString(styles.language);
const buttonClass = validateString(styles.button);

function CodeBlock({ code, language, filename }: CodeBlockProps): ReactElement {
  const onCopyClick = useCallback(() => {
    navigator.clipboard.writeText(code);
    toast.success('Copied code');
  }, [code]);

  return (
    <div className={rootClass}>
      <div className={topBarClass}>
        <div className={macIcons}>
          <span />
          <span />
          <span />
        </div>
        <div className={topBarContentClass}>
          <span className={languageClass}>
            {filename ?? `.${LANGUAGE_TO_EXTENSION[language] ?? language}`}
          </span>
          <IconButton className={buttonClass} onClick={onCopyClick}>
            <ClipboardCopyIcon />
          </IconButton>
        </div>
      </div>
      <div>
        <PrismLight
          customStyle={{
            margin: '0',
            borderRadius: 0,
          }}
          language={LANGUAGE_TO_PRISM_LANGUAGE[language] ?? language}
          style={materialOceanic}
        >
          {code}
        </PrismLight>
      </div>
    </div>
  );
}

export default CodeBlock;
